import {
   Box,
   Button,
   Heading,
   Modal,
   ModalBody,
   ModalCloseButton,
   ModalContent,
   ModalFooter,
   ModalHeader,
   ModalOverlay,
   ModalProps,
   Switch,
   Text,
} from "@chakra-ui/react";
import { useState } from "react";
import { useCookies } from "react-cookie";
import {
   handleAnalyticsCookies,
   handleFunctionalCookies,
   handleRejectAll,
   handleUncategorizedCookies,
} from "../cookie.helper";
import { cookieList } from "../cookies.default";
import CollapseItem from "./collapse-item.component";
import CollapseList from "./collapse-list.component";

export interface CookiePolicyModalProps extends Omit<ModalProps, "children"> {
   showButton?: boolean;
}

const CookiePolicyModal = ({ isOpen, onClose }: CookiePolicyModalProps) => {
   const [functionalCookies, setFunctionalCookies] = useState<boolean>(false);
   const [analyticsCookies, setAnalyticsCookies] = useState<boolean>(false);
   const [uncategorizedCookies, setUncategorizedCookies] =
      useState<boolean>(false);

   const [_, setCookie, removeCookie] = useCookies([
      "cookieyes-consent",
      "_hjAbsoluteSessionInProgress",
      "__cf_bm",
      "__hssc",
      "_hjIncludedInSessionSample_3470175",
      "hubspotutk",
      "__hstc",
      "_hjRecordingLastActivity",
      "_ga",
      "_ga_*",
      "_gat_gtag_UA_*",
      "_gid",
      "_fbp",
      "_hjSession_*",
      "_hjFirstSeen",
      "_hjSessionUser_",
   ]);

   const onSave = () => {
      setCookie("cookieyes-consent", true);
      if (functionalCookies) {
         handleFunctionalCookies(setCookie);
      }
      if (analyticsCookies) {
         handleAnalyticsCookies(setCookie);
      }
      if (uncategorizedCookies) {
         handleUncategorizedCookies(setCookie);
      }
      onClose();
   };

   const onReject = () => {
      setCookie("cookieyes-consent", true);
      handleRejectAll(removeCookie);
      onClose();
   };

   const onAcceptAll = () => {
      setCookie("cookieyes-consent", true);
      handleFunctionalCookies(setCookie);
      handleAnalyticsCookies(setCookie);
      handleUncategorizedCookies(setCookie);
      onClose();
   };

   return (
      <Modal
         isOpen={isOpen}
         isCentered
         scrollBehavior="inside"
         onClose={onClose}
      >
         <ModalOverlay />
         <ModalContent px="1rem" maxW={{ md: "60%" }}>
            <ModalHeader>Cookie Policy</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
               <Box display="grid" gap="1.5rem">
                  <Box>
                     <Heading fontSize={16}>What are cookies?</Heading>
                     <Text>
                        This Cookie Policy explains what cookies are and how we
                        use them, the types of cookies we use i.e., the
                        information we collect using cookies and how that
                        information is used, and how to manage the cookie
                        settings.
                     </Text>
                  </Box>
                  <Text>
                     Cookies are small text files that are used to store small
                     pieces of information. They are stored on your device when
                     the website is loaded on your browser. These cookies help
                     us make the website function properly, make it more secure,
                     provide a better user experience, understand how the
                     website performs, and analyze what works and where it needs
                     improvement.
                  </Text>
                  <Box>
                     <Heading fontSize={16}>How do we use cookies?</Heading>
                     <Text>
                        As with most online services, our website uses
                        first-party and third-party cookies for several
                        purposes. First-party cookies are mostly necessary for
                        the website to function the right way, and they do not
                        collect any of your personally identifiable data.
                     </Text>
                  </Box>
                  <Text>
                     The third-party cookies used on our website are mainly for
                     understanding how the website performs, how you interact
                     with our website, keeping our services secure, providing
                     advertisements that are relevant to you, and all in all
                     providing you with a better and improved user experience
                     and help speed up your future interactions with our
                     website.
                  </Text>
                  <Heading fontSize={16}>Types of Cookies we use</Heading>
                  <CollapseList
                     description="Necessary cookies are required to enable the basic features of this site, such as providing secure log-in or adjusting your consent preferences. These cookies do not store any personally identifiable data."
                     children={
                        <Box display="grid" gap="1rem">
                           {cookieList.necessary.map(
                              (
                                 { cookieName, duration, description },
                                 index
                              ) => (
                                 <CollapseItem
                                    key={`necessary-${index}`}
                                    cookieName={cookieName}
                                    duration={duration}
                                    description={description}
                                 />
                              )
                           )}
                        </Box>
                     }
                     title="Necessary"
                     rightNode={
                        <Box color="green.600" fontWeight={600}>
                           Always active
                        </Box>
                     }
                  />
                  <CollapseList
                     title="Functional"
                     rightNode={
                        <Switch
                           isChecked={functionalCookies}
                           onChange={(e) =>
                              setFunctionalCookies(e.target.checked)
                           }
                        />
                     }
                     description="Functional cookies help perform certain functionalities like sharing the content of the website on social media platforms, collecting feedback, and other third-party features."
                     children={
                        <Box display="grid" gap="1rem">
                           {cookieList.functional.map(
                              (
                                 { cookieName, duration, description },
                                 index
                              ) => (
                                 <CollapseItem
                                    key={`functional-${index}`}
                                    cookieName={cookieName}
                                    duration={duration}
                                    description={description}
                                 />
                              )
                           )}
                        </Box>
                     }
                  />
                  <CollapseList
                     title="Analytics"
                     rightNode={
                        <Switch
                           isChecked={analyticsCookies}
                           onChange={(e) =>
                              setAnalyticsCookies(e.target.checked)
                           }
                        />
                     }
                     description="Analytical cookies are used to understand how visitors interact with the website. These cookies help provide information on metrics such as the number of visitors, bounce rate, traffic source, etc."
                     children={
                        <Box display="grid" gap="1rem">
                           {cookieList.analytics.map(
                              (
                                 { cookieName, duration, description },
                                 index
                              ) => (
                                 <CollapseItem
                                    key={`analytics-${index}`}
                                    cookieName={cookieName}
                                    duration={duration}
                                    description={description}
                                 />
                              )
                           )}
                        </Box>
                     }
                  />
                  <CollapseList
                     title="Uncategorized"
                     rightNode={
                        <Switch
                           isChecked={uncategorizedCookies}
                           onChange={(e) =>
                              setUncategorizedCookies(e.target.checked)
                           }
                        />
                     }
                     description="Functional cookies help perform certain functionalities like sharing the content of the website on social media platforms, collecting feedback, and other third-party features."
                     children={
                        <Box display="grid" gap="1rem">
                           {cookieList.uncategorized.map(
                              (
                                 { cookieName, duration, description },
                                 index
                              ) => (
                                 <CollapseItem
                                    key={`uncategorized-${index}`}
                                    cookieName={cookieName}
                                    duration={duration}
                                    description={description}
                                 />
                              )
                           )}
                        </Box>
                     }
                  />
                  <Box>
                     <Heading fontSize={16}>Manage cookie preferences</Heading>
                     <Text>
                        You can change your cookie preferences at any time by
                        clicking the above button. This will let you revisit the
                        cookie consent banner and change your preferences or
                        withdraw your consent immediately. In addition,
                        different browsers provide different methods to block
                        and delete cookies used by websites. You can change the
                        settings of your browser to block/delete the cookies.
                        Listed below are the links to the support documents on
                        how to manage and delete cookies from significant web
                        browsers.
                     </Text>
                  </Box>
                  <Text>
                     Chrome: https://support.google.com/accounts/answer/32050
                     Safari:
                     https://support.apple.com/en-in/guide/safari/sfri11471/mac
                     Firefox:
                     https://support.mozilla.org/en-US/kb/clear-cookies-and-site-data-firefox?redirectslug=delete-cookies-remove-info-websites-stored&redirectlocale=en-US
                     Internet Explorer:
                     https://support.microsoft.com/en-us/topic/how-to-delete-cookie-files-in-internet-explorer-bca9446f-d873-78de-77ba-d42645fa52fc
                  </Text>
                  <Text>
                     If you are using any other web browser, please visit your
                     browser's official support documents.
                  </Text>
                  <Box>
                     <Heading fontSize={16}>Contact us</Heading>
                     <Text>
                        Don't hesitate to contact us if you have any questions.
                     </Text>
                  </Box>
                  <Box>
                     <Text>Via E-mail: info@nordroof.com</Text>
                     <Text>Via Phone number: +370 655 32605</Text>
                     <Text>
                        Via Website’s Contact Form: https://www.nordroof.com/
                     </Text>
                  </Box>
               </Box>
            </ModalBody>
            <ModalFooter gap="1rem">
               <Button
                  onClick={onReject}
                  variant="outline"
                  colorScheme="brandBlue"
                  color="brandBlue.600"
                  backgroundColor="white"
                  borderWidth="2px"
                  borderColor="brandBlue.600"
                  _hover={{
                     color: "white",
                     backgroundColor: "brandBlue.600",
                  }}
               >
                  Reject All
               </Button>
               <Button
                  onClick={onSave}
                  variant="outline"
                  colorScheme="brandBlue"
                  color="brandBlue.600"
                  backgroundColor="white"
                  borderWidth="2px"
                  borderColor="brandBlue.600"
                  _hover={{
                     color: "white",
                     backgroundColor: "brandBlue.600",
                  }}
               >
                  Save preferences
               </Button>
               <Button
                  onClick={onAcceptAll}
                  variant="solid"
                  colorScheme="brandBlue"
                  borderWidth="2px"
                  borderColor="brandBlue.600"
                  _hover={{
                     color: "brandBlue.600",
                     backgroundColor: "white",
                  }}
               >
                  Accept All
               </Button>
            </ModalFooter>
         </ModalContent>
      </Modal>
   );
};

export default CookiePolicyModal;
